import { useEffect, useState } from "react";
import { adminApi } from "../api.js";
import styles from "../admin.module.css";

interface Category {
  id: number;
  slug: string;
  nameDe: string;
  nameEn: string;
  sort: number;
  active: boolean;
}

const EMPTY_FORM = { slug: "", nameDe: "", nameEn: "", sort: 0, active: true };

export function Categories() {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reload() {
    adminApi.get<Category[]>("/api/admin/categories").then(setCategories);
  }

  useEffect(reload, []);

  function startEdit(c: Category) {
    setEditingId(c.id);
    setForm({ slug: c.slug, nameDe: c.nameDe, nameEn: c.nameEn, sort: c.sort, active: c.active });
  }

  function resetForm() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function handleSubmit() {
    setError(null);
    try {
      if (editingId !== null) {
        await adminApi.put(`/api/admin/categories/${editingId}`, form);
      } else {
        await adminApi.post("/api/admin/categories", form);
      }
      resetForm();
      reload();
    } catch {
      setError("Speichern fehlgeschlagen. Ist der Slug bereits vergeben?");
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm("Kategorie wirklich löschen?")) return;
    await adminApi.delete(`/api/admin/categories/${id}`);
    reload();
  }

  return (
    <div>
      <h1 className={styles.pageTitle}>Kategorien</h1>

      {categories && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Slug</th>
              <th>Name (DE)</th>
              <th>Name (EN)</th>
              <th>Reihenfolge</th>
              <th>Aktiv</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <tr key={c.id}>
                <td>{c.slug}</td>
                <td>{c.nameDe}</td>
                <td>{c.nameEn}</td>
                <td>{c.sort}</td>
                <td>{c.active ? "Ja" : "Nein"}</td>
                <td>
                  <button type="button" className={styles.buttonSecondary} onClick={() => startEdit(c)}>
                    Bearbeiten
                  </button>{" "}
                  <button type="button" className={styles.buttonDanger} onClick={() => handleDelete(c.id)}>
                    Löschen
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={styles.card}>
        <h2>{editingId !== null ? "Kategorie bearbeiten" : "Neue Kategorie"}</h2>
        {error && <div className={styles.errorBox}>{error}</div>}
        <div className={styles.formRow}>
          <label htmlFor="cat-slug">Slug</label>
          <input
            id="cat-slug"
            className={styles.input}
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="cat-de">Name (DE)</label>
          <input
            id="cat-de"
            className={styles.input}
            value={form.nameDe}
            onChange={(e) => setForm({ ...form, nameDe: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="cat-en">Name (EN)</label>
          <input
            id="cat-en"
            className={styles.input}
            value={form.nameEn}
            onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="cat-sort">Reihenfolge</label>
          <input
            id="cat-sort"
            type="number"
            className={styles.input}
            value={form.sort}
            onChange={(e) => setForm({ ...form, sort: Number(e.target.value) })}
          />
        </div>
        <div className={styles.checkboxRow}>
          <input
            id="cat-active"
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
          />
          <label htmlFor="cat-active">Aktiv</label>
        </div>
        <div className={styles.actions}>
          <button type="button" className={styles.button} onClick={handleSubmit}>
            {editingId !== null ? "Speichern" : "Anlegen"}
          </button>
          {editingId !== null && (
            <button type="button" className={styles.buttonSecondary} onClick={resetForm}>
              Abbrechen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
